import React, { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { formatEuropeanDateTime } from '../../utils/dateFormat';
import { adminDetailCardStyle, adminDetailFieldCardStyle } from '../../components/admin/AdminPanelPrimitives';
import { AdminUserSupportActions } from '../../components/admin/AdminUserSupportActions';

const cardStyle = { ...adminDetailCardStyle, padding: 16, borderRadius: 20 };
const detailItemStyle = { ...adminDetailFieldCardStyle, padding: 12 };

const labelStyle = { fontSize: 12, color: '#777', margin: 0, textTransform: 'uppercase', letterSpacing: 0.5 };
const money = (value) => `${Number(value || 0).toFixed(2)}€`;

export const AdminHostDetailView = ({ detail, loading, onBack, onStartConversation, onRefresh, backLabel = 'Back' }) => {
  const host = detail?.host;
  const guestPasses = detail?.guest_passes || [];
  const uniqueGuests = useMemo(() => [...new Set(guestPasses.map((entry) => entry.guest_name).filter(Boolean))], [guestPasses]);

  if (loading) {
    return <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>Loading host details...</p>;
  }

  if (!host) {
    return null;
  }

  return (
    <div data-testid="admin-host-detail-view">
      <button onClick={onBack} data-testid="admin-host-detail-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 700, color: 'var(--accent-purple-strong)', marginBottom: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft size={16} /> {backLabel}
      </button>

      <div style={{ display: 'grid', gap: 14 }}>
        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{host.name || host.username || 'Host'}</p>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#666' }}>{host.email || '—'}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Host status</p>
              <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 700 }}>{host.host_status || '—'}</p>
            </div>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Earning rate</p>
              <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 700 }}>{Number(detail?.earnings_overview?.commission_rate || host.commission_rate || 0).toFixed(1)}%</p>
            </div>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Listings tracked</p>
              <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{detail?.listing_count || 0}</p>
            </div>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Invited guests</p>
              <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{uniqueGuests.length}</p>
            </div>
          </div>
        </div>

        <AdminUserSupportActions
          userId={host.id}
          userEmail={host.email}
          isAdmin={host.role === 'admin' || Boolean(host.is_admin)}
        />

        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Earnings + payouts</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 12 }}>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Total earned</p>
              <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{money(detail?.earnings_overview?.total_earnings)}</p>
            </div>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Pending balance</p>
              <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{money(detail?.payout_history?.balances?.pending_total)}</p>
            </div>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Total paid out</p>
              <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{money(detail?.payout_history?.total_paid_out)}</p>
            </div>
            <div style={detailItemStyle}>
              <p style={labelStyle}>Paid rewards</p>
              <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{money(detail?.payout_history?.total_rewards)}</p>
            </div>
          </div>
          <p style={{ ...detailItemStyle, margin: '12px 0 0', fontSize: 13, color: '#666' }}>
            {detail?.bank_details ? `${detail.bank_details.holder_name} · ${detail.bank_details.iban_masked}` : 'Bank details not provided'}
          </p>
        </div>

        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Recent invited guests</p>
          {guestPasses.length === 0 ? <p style={{ margin: '10px 0 0', color: '#999' }}>No invited guest history yet</p> : guestPasses.slice(0, 8).map((entry) => (
            <div key={entry.id} style={{ ...detailItemStyle, marginTop: 10 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{entry.guest_name || entry.guest_email || 'Guest'}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{entry.listing_name || 'Listing'} · {formatEuropeanDateTime(entry.created_at)}</p>
            </div>
          ))}
        </div>

        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Communication</p>
          <button onClick={() => onStartConversation(host.id, host.name || host.email)} data-testid={`admin-start-host-chat-${host.id}`} style={{ width: '100%', marginTop: 12, border: '1px solid var(--card-border)', borderRadius: 16, padding: '12px 16px', background: 'var(--surface)', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
            Start admin ↔ host chat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHostDetailView;